import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Paper,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { clinicalArchiveService } from '../services/clinicalArchiveService';
import { INSTANT_FORMATS, formatCalendarDate } from '../utils/dateUtils';
import { useFormatInstant } from '../hooks/useClinicTimezone';
import { withOfflineFallback, readByHorse, readActiveByHorse, executeWrite, executeUpdate, executeDelete } from '../offline';
import { useRBAC } from '../auth/useRBAC';
import { patientChartControls } from '../auth/pageControls';

interface Problem {
  id: string;
  horseId: string;
  title: string;
  description?: string;
  status: 'ACTIVE' | 'RESOLVED' | 'MONITORING';
  onset?: string;
  resolvedAt?: string;
  notes?: string;
  createdAt: string;
  isArchived?: boolean;
}

interface Props {
  horseId: string;
}

const statusColor = (status: string): 'error' | 'warning' | 'success' | 'default' => {
  if (status === 'ACTIVE') return 'error';
  if (status === 'MONITORING') return 'warning';
  if (status === 'RESOLVED') return 'success';
  return 'default';
};

const ProblemListPanel: React.FC<Props> = ({ horseId }) => {
  const rbac = useRBAC();
  // Add / Resolve / Monitor / Delete all hit problem-list.controller.ts, which declares
  // manage:medical_records on create :13, update :45 and remove :64. The panel mounts inside
  // both chart layouts on a bare horseId, so it reads the map itself.
  const can = patientChartControls(rbac);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // The INSTANT half of the caption below (createdAt / resolvedAt). The CALENDAR
  // half (onset) must NOT go through here — see the comment at the render site.
  const formatInstant = useFormatInstant();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    onset: '',
    notes: '',
    status: 'ACTIVE',
  });

  const { data: problems = [], isLoading } = useQuery<Problem[]>(
    ['problem-list', horseId],
    withOfflineFallback(
      () => api.get('/problem-list', { params: { horseId } }).then(r => r.data),
      () => readActiveByHorse('problemLists', horseId) as Promise<Problem[]>,
    ),
    { enabled: !!horseId },
  );

  // Archived entries load only when asked for (Delete archives since 2026-10).
  const { data: archived = [] } = useQuery<Problem[]>(
    ['problem-list', horseId, 'archived'],
    withOfflineFallback(
      () => clinicalArchiveService.listArchived<Problem>('problem-list', horseId),
      async () => ((await readByHorse('problemLists', horseId)) as Problem[]).filter(p => p.isArchived),
    ),
    { enabled: !!horseId && showArchived },
  );

  const createMutation = useMutation(
    (payload: any) =>
      executeWrite({
        entityType: 'problemList',
        table: 'problemLists',
        payload,
        networkFn: () => api.post('/problem-list', payload).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['problem-list', horseId]);
        enqueueSnackbar('Problem added', { variant: 'success' });
        setDialogOpen(false);
        setForm({ title: '', description: '', onset: '', notes: '', status: 'ACTIVE' });
      },
      onError: () => { enqueueSnackbar('Failed to add problem', { variant: 'error' }); },
    },
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      executeUpdate({
        entityType: 'problemList',
        table: 'problemLists',
        entityId: id,
        payload: data,
        networkFn: () => api.put(`/problem-list/${id}`, data).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['problem-list', horseId]);
        enqueueSnackbar('Problem updated', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to update problem', { variant: 'error' }); },
    },
  );

  const deleteMutation = useMutation(
    (id: string) =>
      executeDelete({
        entityType: 'problemList',
        table: 'problemLists',
        entityId: id,
        networkFn: () => api.delete(`/problem-list/${id}`).then(r => r.data),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['problem-list', horseId]);
        enqueueSnackbar('Problem archived', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to archive problem', { variant: 'error' }); },
    },
  );

  // Restore is online-only, like every other clinical restore.
  const restoreMutation = useMutation(
    (id: string) => clinicalArchiveService.restore('problem-list', id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['problem-list', horseId]);
        enqueueSnackbar('Problem restored', { variant: 'success' });
      },
      onError: () => { enqueueSnackbar('Failed to restore problem', { variant: 'error' }); },
    },
  );

  const handleSubmit = () => {
    if (!form.title.trim()) {
      enqueueSnackbar('Title is required', { variant: 'warning' });
      return;
    }
    createMutation.mutate({
      horseId,
      title: form.title,
      description: form.description || undefined,
      // WRITE ANCHOR — deliberately UTC MIDNIGHT, not the noon-UTC `ymdToNoonUtcIso`
      // the frontend rule prescribes for a calendar day. `form.onset` is a native
      // `type="date"` string, so `new Date('2026-01-15')` is parsed by spec as UTC
      // midnight and the DAY the user picked survives exactly — this is not the W10
      // browser-local-midnight trap (that one is MUI `<DatePicker>` values handed to
      // axios, which really can store the wrong day). `ProblemList.onset` is a
      // single-anchor column today (DATE-KINDS §1.1, no ⚠), and re-anchoring only this
      // client would fragment it AND break the AI assistant's reader, which treats
      // exactly-UTC-midnight as its CALENDAR discriminator and re-zones anything else
      // (`ai-assistant/agent/datetime.ts` formatLocalDate). Collapsing this column to
      // noon is a cross-surface decision (web + AI + iOS), not a render-burndown edit.
      onset: form.onset ? new Date(form.onset).toISOString() : undefined,
      notes: form.notes || undefined,
      status: form.status,
    });
  };

  const active = problems.filter(p => p.status !== 'RESOLVED');
  const resolved = problems.filter(p => p.status === 'RESOLVED');

  if (isLoading) {
    return <Typography color="textSecondary">Loading problem list...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" color="textSecondary">
          {active.length} active · {resolved.length} resolved
        </Typography>
        {can.problemWrite && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add Problem
          </Button>
        )}
      </Box>

      {problems.length === 0 ? (
        <Box py={4} textAlign="center">
          <Typography color="textSecondary">No problems recorded</Typography>
        </Box>
      ) : (
        <Box>
          {problems.map(problem => (
            <Paper key={problem.id} variant="outlined" sx={{ mb: 1.5, p: 2 }}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle2">{problem.title}</Typography>
                    <Chip label={problem.status} color={statusColor(problem.status)} size="small" />
                  </Box>
                  {problem.description && (
                    <Typography variant="body2" color="textSecondary" mb={0.5}>
                      {problem.description}
                    </Typography>
                  )}
                  {/* THREE VALUES, TWO KINDS — the whole doctrine in one caption, and
                      getting either half backwards is the INVERSE trap
                      (.claude/rules/data-model-landmines.md). Kinds from DATE-KINDS.md §1.1
                      (calendar), §1.2/§1.3 (instant); burndown rows §2.2 and §2.3.

                      · onset      CALENDAR. `problem-list.controller.ts:23,54` stores
                                   `new Date(body.onset)` over the bare `yyyy-MM-dd` this panel
                                   posts at the `type="date"` field below, i.e. UTC MIDNIGHT.
                                   `format(new Date(iso))` re-read that instant in the viewer's
                                   zone, so every negative-offset zone — every US clinic — showed
                                   the PREVIOUS day: a laminitis onset of 9 Dec read as 8 Dec.
                                   Parsing the date PART is zone-invariant by construction.
                      · createdAt  INSTANT (`DateTime @default(now())`). A real moment, whose only
                                   correct zone is the CLINIC'S — the browser's is right only for
                                   someone standing in the clinic, and wrong near midnight for
                                   everyone else.
                      · resolvedAt INSTANT. The column is listed MIXED (§1.2) only because
                                   `problem-list.controller.ts:55` would accept a client value
                                   through an untyped body — that arm has no caller anywhere in
                                   the repo. The one live writer, `problem-list.service.ts:81-82`,
                                   stamps `new Date()` on the RESOLVED transition, which is why
                                   §2.3 groups it with createdAt rather than with onset. */}
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    data-testid={`problem-dates-${problem.id}`}
                  >
                    {problem.onset
                      ? `Onset: ${formatCalendarDate(problem.onset, 'MMM d, yyyy')}`
                      : `Added: ${formatInstant(problem.createdAt, INSTANT_FORMATS.date)}`}
                    {problem.resolvedAt &&
                      ` · Resolved: ${formatInstant(problem.resolvedAt, INSTANT_FORMATS.date)}`}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.5}>
                  {can.problemWrite && problem.status === 'ACTIVE' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => updateMutation.mutate({ id: problem.id, data: { status: 'RESOLVED' } })}
                    >
                      Resolve
                    </Button>
                  )}
                  {can.problemWrite && problem.status === 'ACTIVE' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => updateMutation.mutate({ id: problem.id, data: { status: 'MONITORING' } })}
                    >
                      Monitor
                    </Button>
                  )}
                  {can.problemWrite && problem.status === 'MONITORING' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => updateMutation.mutate({ id: problem.id, data: { status: 'RESOLVED' } })}
                    >
                      Resolve
                    </Button>
                  )}
                  {can.removeClinicalRecord && (
                    <IconButton size="small" aria-label="Archive" onClick={() => deleteMutation.mutate(problem.id)}>
                      <ArchiveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <FormControlLabel
        sx={{ mt: 1 }}
        control={<Switch size="small" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />}
        label="Show archived"
      />
      {showArchived && archived.length === 0 && (
        <Typography variant="body2" color="textSecondary">No archived problems</Typography>
      )}
      {showArchived && archived.map(problem => (
        <Paper key={problem.id} variant="outlined" sx={{ mb: 1.5, p: 2, opacity: 0.7 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2" flex={1}>{problem.title}</Typography>
            <Chip label="Archived" size="small" />
            {can.removeRecord && (
              <Button
                size="small"
                startIcon={<UnarchiveIcon />}
                onClick={() => restoreMutation.mutate(problem.id)}
                disabled={restoreMutation.isLoading}
              >
                Restore
              </Button>
            )}
          </Box>
        </Paper>
      ))}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Problem</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title *"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Status"
              select
              value={form.status}
              onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              fullWidth
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="MONITORING">Monitoring</MenuItem>
            </TextField>
            <TextField
              label="Onset Date"
              type="date"
              value={form.onset}
              onChange={e => setForm(f => ({ ...f, onset: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={createMutation.isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProblemListPanel;
